import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Button } from '@/components/ui';
import { ItineraryItem } from '@/types/cargoList.types';
import { Edit, Clock, GripVertical } from 'lucide-react';

interface SortableItineraryItemProps {
  item: ItineraryItem;
  index: number;
  totalItems: number;
  onEdit?: (item: ItineraryItem) => void;
}

export function SortableItineraryItem({
  item,
  index,
  totalItems,
  onEdit,
}: SortableItineraryItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.order.toString(),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /** Phone-width form - drops the year, which never varies within a voyage. */
  const formatDateTimeShort = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:gap-4 sm:p-4"
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="flex cursor-grab items-center justify-center py-1 text-gray-400 hover:text-gray-600 active:cursor-grabbing dark:text-gray-500 dark:hover:text-gray-300 sm:p-1"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Port Number Indicator */}
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-800 dark:bg-primary-900/50 dark:text-primary-200 sm:h-8 sm:w-8 sm:text-sm">
        {item.order}
      </div>

      <div className="min-w-0 flex-1 space-y-2 sm:space-y-3">
        {/* Port Header. Speed and edit sit beside the port name on a phone rather
            than taking a row of their own. */}
        <div className="flex items-start justify-between gap-2 sm:items-center">
          <div className="min-w-0">
            <h4 className="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
              {item.port}
            </h4>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Badge variant={item.purpose === 'Loading' ? 'success' : 'primary'} size="sm">
                {item.purpose}
              </Badge>
              {item.via && (
                <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                  via {item.via}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1 sm:gap-3">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:text-sm">
              Speed: {item.speed}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit?.(item)}
              className="px-2 text-gray-400 hover:text-primary-600"
              aria-label={`Edit ${item.port}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Timing Information */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
          <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-800/50 sm:gap-3 sm:p-3">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50 sm:h-8 sm:w-8">
              <Clock className="h-3.5 w-3.5 text-red-600 dark:text-red-400 sm:h-4 sm:w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Departure</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-white sm:text-sm">
                <span className="sm:hidden">{formatDateTimeShort(item.departureTime)}</span>
                <span className="hidden sm:inline">{formatDateTime(item.departureTime)}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-800/50 sm:gap-3 sm:p-3">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50 sm:h-8 sm:w-8">
              <Clock className="h-3.5 w-3.5 text-green-600 dark:text-green-400 sm:h-4 sm:w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Arrival</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-white sm:text-sm">
                <span className="sm:hidden">{formatDateTimeShort(item.arrivalTime)}</span>
                <span className="hidden sm:inline">{formatDateTime(item.arrivalTime)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Port Note */}
        {item.note && (
          <div className="rounded-lg bg-blue-50 p-2 dark:bg-blue-900/20 sm:p-3">
            <p className="text-xs text-blue-800 dark:text-blue-300 sm:text-sm">{item.note}</p>
          </div>
        )}
      </div>

      {/* Connection Line for Multiple Ports */}
      {index < totalItems - 1 && (
        <div className="absolute -bottom-2 left-8 h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
      )}
    </div>
  );
}
